import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { translateApiMessage } from "../utils/formErrors";
import { Link, useParams } from "react-router-dom";
import Select from "./ui/Select";
import {
  Archive,
  ArrowRight,
  CheckCircle2,
  Hash,
  Loader2,
  Package,
  Save,
  Scale,
  UserRound,
} from "lucide-react";
import axiosInstance from "../utils/axiosInstance";
import { formatNumber } from "../utils/numberFormat";

const normalizeResults = (payload) =>
  Array.isArray(payload) ? payload : payload?.results || [];

const displayNumber = (value) =>
  value === null || value === undefined || value === ""
    ? "-"
    : formatNumber(value, { maximumFractionDigits: 3 });

const InfoItem = ({ icon: Icon, label, children }) => (
  <div className="border-b border-slate-100 py-3 last:border-b-0">
    <div className="mb-1 flex items-center gap-2 text-xs font-medium text-slate-500">
      <Icon size={15} />
      {label}
    </div>
    <div className="text-sm font-semibold text-slate-900">{children || "-"}</div>
  </div>
);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 12,
    borderColor: state.isFocused ? "#2563eb" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(37, 99, 235, 0.15)" : "none",
    textAlign: "right",
  }),
  menu: (base) => ({ ...base, zIndex: 50, textAlign: "right" }),
  option: (base) => ({ ...base, textAlign: "right" }),
};

const StorageGoodDetails = () => {
  const { goodsId } = useParams();
  const [good, setGood] = useState(null);
  const [storages, setStorages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    storage: "",
    is_sold: false,
    sold_to_customer: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const [goodResponse, storageResponse, customerResponse] =
          await Promise.all([
            axiosInstance.get(`/cottage-goods/${goodsId}/`),
            axiosInstance.get("/storages/", { params: { page_size: 500 } }),
            axiosInstance.get("/accounts/customer_list/", {
              params: { page_size: 500 },
            }),
          ]);

        if (!mounted) return;
        const goodData = goodResponse.data;
        setGood(goodData);
        setStorages(normalizeResults(storageResponse.data));
        setCustomers(normalizeResults(customerResponse.data));
        setForm({
          storage: goodData.storage ? String(goodData.storage) : "",
          is_sold: Boolean(goodData.is_sold),
          sold_to_customer: goodData.sold_to_customer
            ? String(goodData.sold_to_customer)
            : "",
        });
      } catch (err) {
        setError("خطا در دریافت اطلاعات کالا");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [goodsId]);

  const storageOptions = useMemo(
    () => [
      { value: "", label: "تعریف نشده" },
      ...storages.map((storage) => ({
        value: String(storage.id),
        label: storage.storage_name,
      })),
    ],
    [storages],
  );

  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: String(customer.id),
        label: customer.full_name,
      })),
    [customers],
  );

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (form.is_sold && !form.sold_to_customer) {
      setError("برای کالای فروخته‌شده، مشتری را انتخاب کنید.");
      return;
    }

    setSaving(true);
    try {
      const { data } = await axiosInstance.patch(
        `/cottage-goods/${goodsId}/`,
        {
          storage: form.storage ? Number(form.storage) : null,
          is_sold: form.is_sold,
          sold_to_customer:
            form.is_sold && form.sold_to_customer
              ? Number(form.sold_to_customer)
              : null,
        },
      );
      setGood(data);
      setMessage("اطلاعات کالا ذخیره شد.");
    } catch (err) {
      const apiError = err.response?.data?.sold_to_customer?.[0];
      const message = apiError ? translateApiMessage(apiError) : "خطا در ذخیره اطلاعات کالا";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-24 flex justify-center text-slate-600" dir="rtl">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!good) {
    return (
      <div className="mt-24 px-4 font-vazirmatn" dir="rtl">
        <div className="mx-auto max-w-3xl rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
          {error || "کالا یافت نشد."}
        </div>
      </div>
    );
  }

  const cottageNumber = good.cottage?.cottage_number;

  return (
    <div className="mt-24 px-3 pb-10 font-vazirmatn sm:px-6" dir="rtl">
      <div className="mx-auto max-w-6xl space-y-5">
        <header className="border-b border-slate-200 bg-white px-1 py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Link
              to="/storage"
              className="inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              <ArrowRight size={18} />
              بازگشت به کالاهای انبار
            </Link>
            <div className="flex items-center justify-end gap-3 text-right">
              <div>
                <h1 className="text-xl font-extrabold text-slate-900 sm:text-2xl">
                  {good.goods_description || `کالای ${good.goodscode}`}
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  کد کالا: {good.goodscode}
                </p>
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Package size={22} />
              </div>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
              <h2 className="font-extrabold text-slate-900">اطلاعات کالا</h2>
            </div>
            <div className="grid grid-cols-1 gap-x-8 px-5 py-2 sm:grid-cols-2">
              <InfoItem icon={Hash} label="کد کالا">{good.goodscode}</InfoItem>
              <InfoItem icon={Hash} label="HS Code">{good.HSCode}</InfoItem>
              <InfoItem icon={Scale} label="وزن خالص">
                {displayNumber(good.netweight)} کیلوگرم
              </InfoItem>
              <InfoItem icon={Scale} label="وزن ناخالص">
                {displayNumber(good.grossweight)} کیلوگرم
              </InfoItem>
              <InfoItem icon={Package} label="تعداد">
                {displayNumber(good.quantity)}
              </InfoItem>
              <InfoItem icon={Archive} label="کوتاژ">
                {cottageNumber ? (
                  <Link className="text-blue-700 hover:underline" to={`/cottages/${cottageNumber}`}>
                    {cottageNumber}
                  </Link>
                ) : null}
              </InfoItem>
            </div>
          </section>

          <form onSubmit={handleSave} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
              <h2 className="font-extrabold text-slate-900">وضعیت انبار و فروش</h2>
            </div>
            <div className="space-y-5 p-5">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">انبار</label>
                <Select
                  value={storageOptions.find((option) => option.value === form.storage)}
                  onChange={(option) =>
                    setForm((current) => ({ ...current, storage: option?.value || "" }))
                  }
                  options={storageOptions}
                  styles={selectStyles}
                  isClearable
                  placeholder="تعریف نشده"
                />
              </div>

              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
                  <CheckCircle2 size={18} />
                  فروخته شده
                </span>
                <input
                  type="checkbox"
                  checked={form.is_sold}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      is_sold: event.target.checked,
                      sold_to_customer: event.target.checked
                        ? current.sold_to_customer
                        : "",
                    }))
                  }
                  className="h-5 w-5 rounded border-slate-300 text-primary focus:ring-primary"
                />
              </label>

              {form.is_sold ? (
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">
                    مشتری خریدار
                  </label>
                  <Select
                    value={customerOptions.find(
                      (option) => option.value === form.sold_to_customer,
                    ) || null}
                    onChange={(option) =>
                      setForm((current) => ({
                        ...current,
                        sold_to_customer: option?.value || "",
                      }))
                    }
                    options={customerOptions}
                    styles={selectStyles}
                    isClearable
                    placeholder="انتخاب مشتری"
                    noOptionsMessage={() => "مشتری یافت نشد"}
                  />
                </div>
              ) : null}

              {good.is_sold && good.sold_to_customer_detail ? (
                <Link
                  to={`/customers/details/${good.sold_to_customer_detail.id}`}
                  className="flex items-center gap-2 text-sm font-bold text-blue-700 hover:underline"
                >
                  <UserRound size={17} />
                  {good.sold_to_customer_detail.full_name}
                </Link>
              ) : null}

              {error ? (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {error}
                </div>
              ) : null}
              {message ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {message}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={saving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                ذخیره تغییرات
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StorageGoodDetails;
